import { BehaviorSubject, Subscription } from 'rxjs';
import { createPreference } from '@brainfocus/web/shared/storage/preference';

/**
 * UploadProgress tracks the state of an active upload
 */
export interface UploadProgress {
    uploadId: string;
    fileName: string;
    patientId: number;
    progress: number; // 0-100
    status: 'processing' | 'uploading' | 'completed' | 'failed';
    error?: string;
}

/**
 * PersistedUpload is the format stored in localStorage
 * (includes timestamp for cleanup)
 */
interface PersistedUpload extends UploadProgress {
    timestamp: number;
}

/**
 * UploadManager is a singleton that manages active uploads.
 *
 * Features:
 * - Singleton pattern (one instance across the app)
 * - RxJS BehaviorSubject for reactive state updates
 * - Upload lifecycle: start → progress → complete/fail
 * - Automatic cleanup when upload completes
 * - localStorage persistence (survives F5 reload and navigation)
 * - 7-day automatic cleanup of stale uploads
 * - Manages upload subscriptions independently of component lifecycle
 */
/**
 * The uploads to restore after a reload. Its pre-#1166 key
 * (`brainfocus_active_uploads`) is migrated on first read.
 */
const activeUploadsPreference = createPreference<PersistedUpload[]>('active-uploads', {
    version: 1,
    defaultValue: () => [],
    parse: raw => (Array.isArray(raw) ? raw as PersistedUpload[] : undefined),
    legacyKeys: () => ['brainfocus_active_uploads'],
});

export class UploadManager {

    private static instance: UploadManager | null = null;
    private static readonly MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

    private activeUploadsMap: Map<string, UploadProgress>;
    public readonly activeUploads$: BehaviorSubject<Map<string, UploadProgress>>;
    private uploadSubscriptions: Map<string, Subscription>;

    /**
     * Private constructor - use getInstance() instead
     */
    private constructor() {
        this.activeUploadsMap = new Map();
        this.uploadSubscriptions = new Map();

        // Load uploads from localStorage
        this.loadFromStorage();

        this.activeUploads$ = new BehaviorSubject(new Map(this.activeUploadsMap));
    }

    /**
     * Get the singleton instance
     */
    public static getInstance(): UploadManager {
        if (UploadManager.instance === null) {
            UploadManager.instance = new UploadManager();
        }
        return UploadManager.instance;
    }

    /**
     * Reset the singleton instance (for testing purposes only)
     * @internal
     */
    public static resetInstance(): void {
        UploadManager.instance = null;
    }

    /**
     * Get snapshot of active uploads
     */
    public getActiveUploads(): Map<string, UploadProgress> {
        // Return a new Map for immutability, excluding completed uploads
        const activeOnly = new Map<string, UploadProgress>();
        for (const [id, upload] of this.activeUploadsMap) {
            if (upload.status !== 'completed') {
                activeOnly.set(id, upload);
            }
        }
        return activeOnly;
    }

    /**
     * Get specific upload by ID
     */
    public getUpload(uploadId: string): UploadProgress | undefined {
        return this.activeUploadsMap.get(uploadId);
    }

    /**
     * Start processing an EDF file (downsampling stage before upload)
     */
    public startProcessing(uploadId: string, fileName: string, patientId: number): void {
        const upload: UploadProgress = {
            uploadId,
            fileName,
            patientId,
            progress: 0,
            status: 'processing',
        };

        this.activeUploadsMap.set(uploadId, upload);
        this.emitUpdate();
    }

    /**
     * Transition from processing to uploading stage (resets progress to 0)
     */
    public startUploading(uploadId: string): void {
        const upload = this.activeUploadsMap.get(uploadId);
        if (!upload) return;

        upload.status = 'uploading';
        upload.progress = 0;
        this.emitUpdate();
    }

    /**
     * Start a new upload (skips processing stage)
     */
    public startUpload(uploadId: string, fileName: string, patientId: number): void {
        const upload: UploadProgress = {
            uploadId,
            fileName,
            patientId,
            progress: 0,
            status: 'uploading',
        };

        this.activeUploadsMap.set(uploadId, upload);
        this.emitUpdate();
    }

    /**
     * Update upload progress
     * @param progress - Progress value in either decimal (0.0-1.0) or percentage (0-100) format.
     *                   Values <= 1.0 are interpreted as decimal fractions (e.g., 0.75 = 75%).
     *                   Values > 1.0 are interpreted as percentages (e.g., 75 = 75%).
     *                   This dual format supports typical XHR progress events (decimal)
     *                   and manual percentage updates.
     */
    public updateProgress(uploadId: string, progress: number): void {
        const upload = this.activeUploadsMap.get(uploadId);
        if (!upload) {
            return; // Silently ignore non-existent uploads
        }

        // Normalize progress to 0-100 percentage range
        // Decision boundary at 1.0: values <= 1.0 are decimal (0.0-1.0), values > 1.0 are percentage (0-100)
        // Examples: 0.58254 → 58%, 0.75 → 75%, 58 → 58%, 100 → 100%
        // Edge case: If someone passes 0.5 meaning "0.5%", this will interpret as 50%.
        // This is acceptable because XHR APIs always use decimal format (0.0-1.0).
        const normalizedProgress = progress <= 1 ? progress * 100 : progress;

        // Clamp to 0-100 range and round to integer for display
        upload.progress = Math.max(0, Math.min(100, Math.round(normalizedProgress)));
        this.emitUpdate();
    }

    /**
     * Complete an upload (removes from active automatically)
     */
    public completeUpload(uploadId: string): void {
        const upload = this.activeUploadsMap.get(uploadId);
        if (!upload) {
            return; // Silently ignore non-existent uploads
        }

        // Cleanup subscription
        this.cleanupSubscription(uploadId);

        // Remove from active uploads (transparent cleanup)
        this.activeUploadsMap.delete(uploadId);
        this.emitUpdate();
    }

    /**
     * Mark upload as failed (keeps in active so user can see error)
     */
    public failUpload(uploadId: string, error: string): void {
        const upload = this.activeUploadsMap.get(uploadId);
        if (!upload) {
            return; // Silently ignore non-existent uploads
        }

        // Cleanup subscription
        this.cleanupSubscription(uploadId);

        upload.status = 'failed';
        upload.error = error;
        this.emitUpdate();
    }

    /**
     * Remove upload from active uploads
     */
    public removeUpload(uploadId: string): void {
        // Cleanup subscription
        this.cleanupSubscription(uploadId);

        this.activeUploadsMap.delete(uploadId);
        this.emitUpdate();
    }

    /**
     * Register a subscription for an upload
     * This allows the manager to cleanup subscriptions when uploads complete/fail
     */
    public registerSubscription(uploadId: string, subscription: Subscription): void {
        // Cleanup any existing subscription for this uploadId
        this.cleanupSubscription(uploadId);

        this.uploadSubscriptions.set(uploadId, subscription);
    }

    /**
     * Cleanup subscription for an upload
     */
    private cleanupSubscription(uploadId: string): void {
        const subscription = this.uploadSubscriptions.get(uploadId);
        if (subscription) {
            subscription.unsubscribe();
            this.uploadSubscriptions.delete(uploadId);
        }
    }

    /**
     * Emit update via BehaviorSubject (creates new Map for immutability)
     */
    private emitUpdate(): void {
        // Emit only non-completed uploads (same as getActiveUploads())
        const activeOnly = new Map<string, UploadProgress>();
        for (const [id, upload] of this.activeUploadsMap) {
            if (upload.status !== 'completed') {
                activeOnly.set(id, upload);
            }
        }
        this.activeUploads$.next(activeOnly);

        // Save to localStorage after each state change
        this.saveToStorage();
    }

    /**
     * Load uploads from localStorage and clean up stale ones (>7 days)
     * Marks uploads that were 'uploading' as 'failed' (they're zombie uploads from F5/close)
     */
    private loadFromStorage(): void {
        try {
            // Absent, corrupt or not a list → none (the preference never throws).
            const uploads = activeUploadsPreference.load();

            const now = Date.now();
            const cutoff = now - UploadManager.MAX_AGE_MS;

            for (const upload of uploads) {
                // Skip uploads older than 7 days
                // Use < instead of <= to keep uploads that are exactly at the 7-day boundary
                if (upload.timestamp < cutoff) {
                    continue;
                }

                // Don't restore completed uploads (they're already done)
                if (upload.status === 'completed') {
                    continue;
                }

                // Detect zombie uploads: if status was 'uploading' or 'processing',
                // the page was refreshed/closed during operation, so mark as failed
                const isZombie = upload.status === 'uploading' || upload.status === 'processing';

                // Restore upload to active map
                this.activeUploadsMap.set(upload.uploadId, {
                    uploadId: upload.uploadId,
                    fileName: upload.fileName,
                    patientId: upload.patientId,
                    progress: upload.progress,
                    status: isZombie ? 'failed' : upload.status,
                    error: isZombie
                        ? 'Upload interrupted (page was refreshed or closed). Please upload again.'
                        : upload.error,
                });
            }
        } catch (e) {
            // Ignore errors (corrupted data, etc.)
            // Just start with empty state
            console.warn('Failed to load uploads from localStorage:', e);
        }
    }

    /**
     * Save current uploads to localStorage
     */
    private saveToStorage(): void {
        try {
            const uploads: PersistedUpload[] = [];
            const now = Date.now();

            for (const upload of this.activeUploadsMap.values()) {
                // Don't save completed uploads
                if (upload.status === 'completed') {
                    continue;
                }

                uploads.push({
                    ...upload,
                    timestamp: now,
                });
            }

            // Best effort: on a full or denied storage the upload keeps working in memory.
            activeUploadsPreference.save(uploads);
        } catch (e) {
            console.warn('Failed to save uploads to localStorage:', e);
        }
    }

}
